import {
  keepPreviousData,
  queryOptions,
  useQuery,
  useSuspenseQuery,
  type UseSuspenseQueryResult,
} from '@tanstack/react-query'
import { useEffect, useState } from 'react'

import { watchlistKeys } from '@/features/watchlist/api/keys'
import { watchlistService } from '@/features/watchlist/api/watchlistService'
import type { PopularStocks, StockCandidate, WatchlistCollection } from '@/schemas/watchlist'

/**
 * What the Home · Empty frame shows where the watchlist card would be — and
 * the rail's Popular stocks card, which the `_app` loader prefetches through
 * this factory (see `prefetchMarketRail`).
 */
export const popularStocksQueryOptions = () =>
  queryOptions<PopularStocks>({
    queryKey: watchlistKeys.popular(),
    queryFn: () => watchlistService.popular(),
  })

export const usePopularStocks = () => useQuery(popularStocksQueryOptions())

export const watchlistCollectionQueryOptions = () =>
  queryOptions<WatchlistCollection>({
    queryKey: watchlistKeys.collection(),
    queryFn: () => watchlistService.collection(),
  })

export const useWatchlistCollection = () => useQuery(watchlistCollectionQueryOptions())

/**
 * The Watchlist screen's read — the route's MAIN data, awaited by its loader,
 * so `data` is always defined, and a failed refetch keeps the lists on screen
 * rather than swapping them for an error. Home, the sidebar, Alerts and Stock
 * still read the same entry through `useWatchlistCollection`, where it is one
 * card's data among many and a pending or failed read is theirs to draw.
 */
export const useSuspenseWatchlistCollection = (): UseSuspenseQueryResult<WatchlistCollection> =>
  useSuspenseQuery(watchlistCollectionQueryOptions())

/**
 * Below this, no request goes out. One character IS a search here — mm-be's
 * `q` is `min_length=1` and the query is bounded and ranked
 * (`services/market/repository.py:498-521`) — so the floor is only "the box
 * has something in it". Keeping the floor at 1 is what lets the picker switch
 * to the server the moment a member types, with no in-between state where it
 * is still filtering 200 rows in memory.
 */
const SEARCH_MIN_LENGTH = 1

/** One pause in typing. */
const SEARCH_DEBOUNCE_MS = 250

export type CandidateSearchState =
  /** Nothing typed. Nothing was asked, so nothing is missing — show "Popular now". */
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'ready'; rows: StockCandidate[] }
  | { status: 'failed'; retry: () => void; retrying: boolean }

/**
 * The "Add to watchlist" picker's search, debounced.
 *
 * Mirrors `useSymbolSearch` (src/screens/portfolio/components/add-transaction)
 * in shape rather than sharing it: that hook caps at 8 rows for a combobox,
 * where this one keeps the full page. Both now exclude the same two index
 * types (`INDEX`, `SUB_INDEX`) rather than allow-listing `STOCK`. The two want
 * to be one hook in `features/market/api/`; that is a hoist across two folders
 * this build does not own, so it is reported rather than done here.
 *
 * The effect only ever SCHEDULES. What is being searched right now is DERIVED
 * from what is typed and what has settled, never stored — so a cleared box
 * cannot leak the previous term's rows back onto a list the member just
 * emptied, and there is no synchronous `setState` in an effect body for
 * `react-hooks/set-state-in-effect` to catch.
 */
export const useCandidateSearch = (term: string): CandidateSearchState => {
  const trimmed = term.trim()
  const [settled, setSettled] = useState('')

  useEffect(() => {
    if (trimmed.length < SEARCH_MIN_LENGTH) return
    const timer = setTimeout(() => {
      setSettled(trimmed)
    }, SEARCH_DEBOUNCE_MS)
    return () => {
      clearTimeout(timer)
    }
  }, [trimmed])

  // Held at the LAST settled term while a new one is still debouncing, not
  // blanked: the key does not change until the pause lands, so the rows the
  // member is looking at stay under the box they are typing in instead of
  // being replaced by skeletons on every keystroke. An empty box does blank
  // it, because an empty box is not a search.
  const active = trimmed.length < SEARCH_MIN_LENGTH ? '' : settled
  const enabled = active.length >= SEARCH_MIN_LENGTH

  const { data, isPending, isError, isFetching, refetch } = useQuery<StockCandidate[]>({
    queryKey: watchlistKeys.candidateSearch(active),
    // `({ client })`, not a bare call: `searchCandidates` reads through
    // `marketEndpoints`, which falls back to the app's module singleton when
    // handed nothing (see src/api/endpointCache.ts). React Query hands the
    // queryFn the client that OWNS this query, so the per-endpoint cache entry
    // lands on the same client as the entry above it — which is what keeps a
    // test on its own client (`test/query.ts` builds one per test) from being
    // answered out of the singleton's 10s-fresh entry from a previous test.
    queryFn: ({ client }) => watchlistService.searchCandidates(active, client),
    enabled,
    // A pager-less list, but the same reason: swapping one term's rows for the
    // next under a list that stays put beats blanking it on every keystroke.
    placeholderData: keepPreviousData,
  })

  if (trimmed.length < SEARCH_MIN_LENGTH) return { status: 'idle' }
  // Typed, but the first pause has not landed yet. Reporting this as `idle`
  // would flash the 200-row "Popular now" list between keystrokes — the very
  // list this hook exists to stop the picker filtering.
  if (!enabled) return { status: 'loading' }
  if (isError) {
    return { status: 'failed', retry: () => void refetch(), retrying: isFetching }
  }
  if (isPending) return { status: 'loading' }
  return { status: 'ready', rows: data }
}
